import _ from 'lodash'
import InvoicePaymentsViewGrid from "../../components/grids/InvoicePaymentsGrid"
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons'
import { AgTechFormNumberControl } from '@agtech/web/components/Forms/Controls/AgTechFormNumberControl'
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl'
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow'
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection'
import { Payment, DefaultPayment } from '@sales/data/invoicing/payments/InvoicePaymentData'
import { usePaymentDataWritePaymentAction } from '@sales/data/invoicing/payments/InvoicePaymentDataActions'
import { useInvoiceDataGetInvoiceDetailsQuery } from '@sales/data/invoicing/InvoicingDataQueries'
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog'
import InvoicePaymentTypeSelectionControl from '../../components/forms/InvoicePaymentTypeSelectionControl'
import { AgTechIndex } from '@agtech/core/utilities/AgTechUtilities'
import { useAgTechQuery, useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody'
import { AgTechContact } from '@agtech/data/contacts/ContactData'
import { ContactDataGetAllContactsQuery } from '@agtech/data/contacts/ContactDataQueries'
import ContactSelectionControl from '@agtech/web/integrations/contacts/components/selections/ContactSelectionControl'
import { AgTechFormReadOnlyTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl'
import dayjs from 'dayjs'
import { InvoiceStatus } from '@sales/data/invoicing/InvoicingData'

export type AddPaymentDialogProps = {
    invoiceId: number
}

type PaymentFormData = {
    contacts: AgTechIndex<AgTechContact>,
    payment: Payment,
    previousPayments: Payment[]
}

const AddPaymentDialog = (props: AddPaymentDialogProps) => {
    const dialogQuery = useAddPaymentsDialogQuery({
        invoiceId: props.invoiceId
    });

    const dialogAction = usePaymentDataWritePaymentAction();
    
    return (
        <AgTechFormDialog
            width={600}
            dialogData={{
                query: dialogQuery,
                getDialogData: p => {
                    const paymentsMade = p.invoiceDetails.invoice.details.paymentsMade;

                    const formData: PaymentFormData = {
                        contacts: p.contacts,
                        payment: {
                            ...DefaultPayment,
                            payerId: p.invoiceDetails.contact.id,
                            invoiceId: p.invoiceDetails.invoice.id,
                            amount: p.invoiceDetails.invoice.info.total
                        },
                        previousPayments: [...paymentsMade]
                    }
                    
                    if (paymentsMade.length > 0)
                    {
                        formData.payment.amount = formData.payment.amount - _.sum(paymentsMade.map(paym => paym.amount));
                    }

                    return formData;
                },
                getDialogContext: p => ({
                    invoiceDetails: p.invoiceDetails
                })
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d.payment
            }}
            structure={(data) => ({
                header: 'Add Payment - Invoice #' + data.context.invoiceDetails.invoice.info.number,
                submitButtonText: 'Create',
            })}
         >
            {(formContext) => (
                <AgTechFormBody>
                    <AgTechFormRow>
                        <AgTechFormReadOnlyTextControl
                            entityType={formContext.entity}
                            caption='Invoice Number'
                            value={() => '#' + formContext.context.invoiceDetails.invoice.info.number}
                            classes='flexed'
                        />
                        <AgTechFormReadOnlyTextControl
                            entityType={formContext.entity}
                            caption='Invoice Date'
                            value={() => dayjs(formContext.context.invoiceDetails.invoice.info.date).format('MM/DD/YYYY')}
                            classes='flexed'
                        />
                        <AgTechFormReadOnlyTextControl
                            entityType={formContext.entity}
                            caption='Status'
                            value={() => InvoiceStatus[formContext.context.invoiceDetails.invoice.info.status]}
                            classes='flexed'
                        />
                    </AgTechFormRow>
                    <AgTechFormRow>
                        <ContactSelectionControl
                            formEntity={formContext.entity}
                            control={{
                                caption: 'Payer',
                                isRequired: true,
                                width: 65}
                            }
                            contacts={{
                                getSelectedContactId: e => e.payment.payerId,
                                onContactSelected: async (e, v) => {
                                    e.payment.payerId = v?.id ?? 0;
                                    return e;
                                }
                            }}
                        />
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption="Amount"
                            required={true}
                            value={entity => entity.payment.amount}
                            onControlValueChanged={async (e, val) => {
                                e.payment.amount = val;
                                return e;
                            }}
                            width={35}
                            unit="$"
                        />
                    </AgTechFormRow>
                    <AgTechFormRow>
                        <InvoicePaymentTypeSelectionControl
                            formContext={formContext}
                            getSelectedPaymentType={entity => entity.payment.paymentType.toString()}
                            onPaymentTypeSelected={async (e, v) => {
                                e.payment.paymentType = v?.id ?? 0;
                                return e;
                            }}
                        />
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="Details"
                            required={false}
                            value={entity => entity.payment.details}
                            onControlValueChanged={async (e, val) => {
                                e.payment.details = val;

                                return e;
                            }}
                        />
                    </AgTechFormRow>
                    {(formContext.entity.previousPayments.length > 0) && (
                        <AgTechFormSection
                            header="Previous Payments"
                            classes='flexed'
                            bodyClasses='flexed pt-3'
                        >
                            <InvoicePaymentsViewGrid
                                invoiceId={props.invoiceId}
                                contacts={formContext.entity.contacts}
                                payments={formContext.entity.previousPayments}
                            />
                        </AgTechFormSection>
                    )}
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

const useAddPaymentsDialogQuery = (props: AddPaymentDialogProps) => {
    const invoiceDetailsQuery = useInvoiceDataGetInvoiceDetailsQuery({
        invoiceId: props.invoiceId
    });

    const contactsQuery = useAgTechQuery({
        query: ContactDataGetAllContactsQuery
    });

    return useAgTechQueryResultCombination({
        firstQuery: invoiceDetailsQuery,
        secondQuery: contactsQuery,
        projection: (invoiceDetails, contactData) => ({
            invoiceDetails: invoiceDetails,
            contacts: contactData
        })
    });
};

AddPaymentDialog.Open = (e: AgTechClickEventHandler, props: AddPaymentDialogProps) => e.dialogs.openDialog({
    dialog: () => <AddPaymentDialog {...props} />
});

AddPaymentDialog.Button = (props: AgTechButtonProps & AddPaymentDialogProps) => {
    let buttonProps: AgTechButtonProps = {
        text: 'Add Payment',
        icon: 'bi bi-credit-card-2-front-fill',
        ...props,
        action: e => AddPaymentDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default AddPaymentDialog;